import { type ReactNode, type FC, useEffect, useRef, useState } from 'react';
import { type HTMLMotionProps, motion } from 'motion/react';

interface AnimateHeightChange {
  children: ReactNode;
  className?: string;
  transition?: HTMLMotionProps<'div'>['transition'];
  disable?: boolean;
}

const animateHeightChangeTransition = {
  bounce: 0,
  duration: 0.15,
  ease: 'easeOut' as const,
  damping: 20,
  stiffness: 150,
  mass: 0.4,
};

export const AnimateHeightChange: FC<AnimateHeightChange> = ({
  children,
  className,
  transition = animateHeightChangeTransition,
  disable = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | 'auto'>('auto');

  useEffect(() => {
    let resizeObserver: ResizeObserver | undefined;

    if (containerRef.current) {
      resizeObserver = new ResizeObserver((entries) => {
        const observedHeight = entries[0].contentRect.height;
        setHeight(disable ? 'auto' : observedHeight);
      });

      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver?.disconnect();
    };
  }, [disable]);

  return (
    <motion.div
      className={className}
      style={{ height }}
      animate={{ height }}
      transition={transition}
    >
      <div ref={containerRef}>{children}</div>
    </motion.div>
  );
};
